// ==============================
// Imports
// ==============================

// MUI Components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  Fab,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SearchIcon from '@mui/icons-material/Search';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

// Libraries
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Local components
import AppBarSpacer from '../components/common/AppBarSpacer';
import AssessmentMiniDrawer from '../components/AssessmentMiniDrawer';
import MiniDrawer from '../components/MiniDrawer';
import PageHeader from '../components/common/PageHeader';
import { useIsDownLg } from '../hooks/useResponsive';
import { useTranslation } from '../i18n';
import {
  USER_GUIDE_VERSION,
  UserGuideAudience,
  UserGuideBlock,
  UserGuideChapter,
  UserGuideFigure,
  userGuideChapters,
} from '../data/userGuideContent';

// ==============================
// Constants
// ==============================
const IMAGE_BASE = `${process.env.PUBLIC_URL || ''}/user-guide`;
// Keeps a scrolled-to heading clear of the fixed 64px AppBar.
const SCROLL_MARGIN = 88;

type RoleFilter = 'all' | 'admin' | 'coach' | 'public';

/** Chapter groups, in reading order. `color` is the group's accent. */
const AUDIENCES: {
  key: UserGuideAudience;
  label: string;
  description: string;
  color: string;
}[] = [
  {
    key: 'general',
    label: 'Chung',
    description: 'Dành cho mọi người dùng: giới thiệu, phân quyền, đăng nhập.',
    color: '#607d8b',
  },
  {
    key: 'admin',
    label: 'Admin — Quản trị viên',
    description: 'Nhân viên quầy và quản lý: nghiệp vụ hằng ngày và Cài đặt.',
    color: '#1976d2',
  },
  {
    key: 'coach',
    label: 'Coach — Huấn luyện viên',
    description: 'Đánh giá level, lớp được giao, điểm danh, xếp lớp.',
    color: '#2e7d32',
  },
  {
    key: 'public',
    label: 'Public — Khách hàng / Phụ huynh',
    description:
      'Trang không cần đăng nhập: khảo sát qua QR, tra cứu học viên.',
    color: '#ed6c02',
  },
  {
    key: 'reference',
    label: 'Tham khảo',
    description: 'Quy trình, quy tắc nghiệp vụ, câu hỏi thường gặp, thuật ngữ.',
    color: '#757575',
  },
];

const ROLE_FILTERS: { value: RoleFilter; label: string }[] = [
  { value: 'all', label: 'Tất cả' },
  { value: 'admin', label: 'Admin' },
  { value: 'coach', label: 'Coach' },
  { value: 'public', label: 'Public' },
];

const audienceOf = (key: UserGuideAudience) =>
  AUDIENCES.find((a) => a.key === key) ?? AUDIENCES[0];

/** Audiences each signed-in role may read. Coaches only get their own
 * chapters and the public (parent/customer) pages. */
const ADMIN_AUDIENCES: UserGuideAudience[] = AUDIENCES.map((a) => a.key);
const COACH_AUDIENCES: UserGuideAudience[] = ['coach', 'public'];

/**
 * A chapter is shown when the signed-in role may read it and it matches the
 * TOC filter (a filter keeps its own group plus the shared general/reference
 * ones, when those are allowed).
 */
const isVisibleFor = (
  chapter: UserGuideChapter,
  role: RoleFilter,
  allowed: UserGuideAudience[],
) =>
  allowed.includes(chapter.audience) &&
  (role === 'all' ||
    chapter.audience === role ||
    chapter.audience === 'general' ||
    chapter.audience === 'reference');

// ==============================
// Helpers
// ==============================

/** Renders **bold**, *italic* and `code` markers used by the guide content. */
const InlineText: React.FC<{ text: string }> = ({ text }) => {
  const parts = text
    .split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`)/g)
    .filter(Boolean);
  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        if (part.length > 2 && part.startsWith('*') && part.endsWith('*')) {
          return <em key={index}>{part.slice(1, -1)}</em>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <Box
              key={index}
              component="code"
              sx={{
                px: 0.5,
                borderRadius: 0.5,
                fontSize: '0.85em',
                bgcolor: 'action.hover',
              }}
            >
              {part.slice(1, -1)}
            </Box>
          );
        }
        return <Fragment key={index}>{part}</Fragment>;
      })}
    </>
  );
};

/** Plain text of a content string, used for TOC search. */
const plain = (text: string) => text.replace(/\*|`/g, '').toLowerCase();

const blockText = (block: UserGuideBlock): string => {
  switch (block.type) {
    case 'paragraph':
    case 'subheading':
    case 'note':
      return block.text;
    case 'bullets':
      return block.items.join(' ');
    case 'table':
      return [...block.headers, ...block.rows.flat()].join(' ');
    case 'figure':
      return block.caption;
    case 'steps':
      return block.items
        .map((s) =>
          [s.text, ...(s.sub || []), s.figure?.caption || ''].join(' '),
        )
        .join(' ');
    default:
      return '';
  }
};

// ==============================
// Content blocks
// ==============================
const GuideFigure: React.FC<{
  figure: UserGuideFigure;
  compact?: boolean;
  onZoom: (figure: UserGuideFigure) => void;
}> = ({ figure, compact, onZoom }) => (
  <Box
    component="figure"
    sx={{
      m: 0,
      my: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}
  >
    <Tooltip title="Nhấn để phóng to">
      <Box
        component="button"
        type="button"
        onClick={() => onZoom(figure)}
        sx={{
          position: 'relative',
          p: 0,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          cursor: 'zoom-in',
          bgcolor: 'background.paper',
          width: '100%',
          maxWidth: compact ? 520 : 960,
          '&:hover .zoom-hint': { opacity: 1 },
        }}
      >
        <Box
          component="img"
          src={`${IMAGE_BASE}/${figure.img}.jpg`}
          alt={figure.caption}
          loading="lazy"
          sx={{
            display: 'block',
            width: '100%',
            height: 'auto',
            // Reserve the final height before the lazy image loads, so TOC
            // scroll targets don't drift as screenshots above them arrive.
            // (Box treats `width`/`height` props as styles, not img attributes.)
            aspectRatio: `${figure.width} / ${figure.height}`,
          }}
        />
        <Box
          className="zoom-hint"
          sx={{
            position: 'absolute',
            right: 8,
            bottom: 8,
            opacity: 0,
            transition: 'opacity .2s',
            bgcolor: 'rgba(0,0,0,.55)',
            color: '#fff',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
          }}
        >
          <ZoomInIcon fontSize="small" />
        </Box>
      </Box>
    </Tooltip>
    <Typography
      component="figcaption"
      variant="caption"
      color="text.secondary"
      sx={{ mt: 0.75, fontStyle: 'italic', textAlign: 'center' }}
    >
      <Box component="span" sx={{ fontWeight: 700, color: 'secondary.main' }}>
        {figure.label}.
      </Box>{' '}
      {figure.caption}
    </Typography>
  </Box>
);

const GuideNote: React.FC<{ kind: 'note' | 'tip' | 'warn'; text: string }> = ({
  kind,
  text,
}) => {
  const theme = useTheme();
  const color =
    kind === 'warn'
      ? theme.palette.warning.main
      : kind === 'tip'
        ? theme.palette.success.main
        : theme.palette.info.main;
  const label =
    kind === 'warn'
      ? 'Lưu ý quan trọng: '
      : kind === 'tip'
        ? 'Mẹo: '
        : 'Ghi chú: ';
  return (
    <Box
      sx={{
        my: 1.5,
        px: 2,
        py: 1.25,
        borderLeft: 4,
        borderColor: color,
        bgcolor: alpha(color, 0.08),
        borderRadius: 1,
      }}
    >
      <Typography variant="body2">
        <Box component="span" sx={{ fontWeight: 700, color }}>
          {label}
        </Box>
        <InlineText text={text} />
      </Typography>
    </Box>
  );
};

const GuideBlockView: React.FC<{
  block: UserGuideBlock;
  onZoom: (figure: UserGuideFigure) => void;
}> = ({ block, onZoom }) => {
  switch (block.type) {
    case 'paragraph':
      return (
        <Typography variant="body1" sx={{ my: 1.25, lineHeight: 1.75 }}>
          <InlineText text={block.text} />
        </Typography>
      );
    case 'subheading':
      return (
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ mt: 2.5, mb: 1 }}
        >
          {block.text}
        </Typography>
      );
    case 'bullets':
      return (
        <Box component="ul" sx={{ my: 1, pl: 3, listStyleType: 'disc' }}>
          {block.items.map((item, index) => (
            <Typography
              component="li"
              key={index}
              variant="body1"
              sx={{ mb: 0.5, lineHeight: 1.7 }}
            >
              <InlineText text={item} />
            </Typography>
          ))}
        </Box>
      );
    case 'note':
      return <GuideNote kind={block.kind} text={block.text} />;
    case 'table':
      return (
        <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {block.headers.map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontWeight: 700,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {block.rows.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                >
                  {row.map((cell, cellIndex) => (
                    <TableCell key={cellIndex} sx={{ verticalAlign: 'top' }}>
                      <InlineText text={cell} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    case 'figure':
      return <GuideFigure figure={block} onZoom={onZoom} />;
    case 'steps':
      return (
        <Box sx={{ my: 1.5 }}>
          {block.items.map((step, index) => (
            <Box key={index} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Chip
                  label={`Bước ${index + 1}`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 700, flexShrink: 0, mt: 0.25 }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.75 }}>
                    <InlineText text={step.text} />
                  </Typography>
                  {step.sub && (
                    <Box
                      component="ul"
                      sx={{ my: 0.5, pl: 3, listStyleType: 'circle' }}
                    >
                      {step.sub.map((item, subIndex) => (
                        <Typography
                          component="li"
                          key={subIndex}
                          variant="body2"
                          sx={{ mb: 0.25, lineHeight: 1.7 }}
                        >
                          <InlineText text={item} />
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
              {step.figure && (
                <GuideFigure
                  figure={step.figure}
                  compact={step.compact}
                  onZoom={onZoom}
                />
              )}
            </Box>
          ))}
        </Box>
      );
    default:
      return null;
  }
};

// ==============================
// Table of contents
// ==============================
const GuideToc: React.FC<{
  chapters: UserGuideChapter[];
  activeId: string;
  query: string;
  role: RoleFilter;
  filters: { value: RoleFilter; label: string }[];
  onRoleChange: (value: RoleFilter) => void;
  onQueryChange: (value: string) => void;
  onSelect: (id: string) => void;
}> = ({
  chapters,
  activeId,
  query,
  role,
  filters,
  onRoleChange,
  onQueryChange,
  onSelect,
}) => {
  const activeChapter = activeId.split('-')[0];
  const groups = AUDIENCES.map((audience) => ({
    ...audience,
    chapters: chapters.filter((c) => c.audience === audience.key),
  })).filter((group) => group.chapters.length > 0);
  return (
    <Box>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={role}
        onChange={(_, value: RoleFilter | null) => value && onRoleChange(value)}
        aria-label="Lọc theo vai trò"
        sx={{ mb: 1 }}
      >
        {filters.map((filter) => (
          <ToggleButton
            key={filter.value}
            value={filter.value}
            sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 700 }}
          >
            {filter.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <TextField
        size="small"
        fullWidth
        placeholder="Tìm trong hướng dẫn..."
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        sx={{ mb: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Xóa tìm kiếm"
                onClick={() => onQueryChange('')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />
      {chapters.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          Không tìm thấy mục phù hợp.
        </Typography>
      )}
      <List dense disablePadding component="nav" aria-label="Mục lục hướng dẫn">
        {groups.map((group) => (
          <Box key={group.key} sx={{ mb: 1 }}>
            <ListSubheader
              disableSticky
              component="button"
              onClick={() => onSelect(`grp-${group.key}`)}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 1,
                px: 1,
                lineHeight: 1.4,
                py: 0.75,
                border: 0,
                cursor: 'pointer',
                bgcolor: alpha(group.color, 0.1),
                borderLeft: `4px solid ${group.color}`,
                borderRadius: 1,
                color: group.color,
                fontWeight: 800,
                fontSize: '0.78rem',
                textTransform: 'uppercase',
                letterSpacing: 0.3,
                textAlign: 'left',
              }}
            >
              <Box component="span" sx={{ flex: 1 }}>
                {group.label}
              </Box>
              <Chip
                label={group.chapters.length}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.7rem',
                  bgcolor: group.color,
                  color: '#fff',
                }}
              />
            </ListSubheader>
            {group.chapters.map((chapter) => {
              const expanded = !!query || chapter.id === activeChapter;
              return (
                <Box key={chapter.id}>
                  <ListItemButton
                    selected={activeId === chapter.id}
                    onClick={() => onSelect(chapter.id)}
                    sx={{
                      borderRadius: 1,
                      py: 0.5,
                      ml: 0.5,
                      borderLeft: `2px solid ${alpha(group.color, 0.35)}`,
                    }}
                  >
                    <ListItemText
                      primary={`${chapter.no}. ${chapter.title}`}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: chapter.id === activeChapter ? 700 : 500,
                        color:
                          chapter.id === activeChapter
                            ? 'secondary'
                            : 'text.primary',
                      }}
                    />
                  </ListItemButton>
                  <Collapse
                    in={expanded && chapter.sections.length > 0}
                    unmountOnExit
                  >
                    <List dense disablePadding>
                      {chapter.sections.map((section) => (
                        <ListItemButton
                          key={section.id}
                          selected={activeId === section.id}
                          onClick={() => onSelect(section.id)}
                          sx={{
                            pl: 3,
                            py: 0.25,
                            ml: 0.5,
                            borderRadius: 1,
                            borderLeft: `2px solid ${alpha(group.color, 0.35)}`,
                          }}
                        >
                          <ListItemText
                            primary={section.title}
                            primaryTypographyProps={{
                              variant: 'caption',
                              fontSize: '0.8rem',
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        ))}
      </List>
    </Box>
  );
};

/** Group divider shown in the content before each audience's first chapter. */
const AudienceBanner: React.FC<{ audience: UserGuideAudience }> = ({
  audience,
}) => {
  const group = audienceOf(audience);
  return (
    <Box
      id={`grp-${group.key}`}
      data-guide-anchor
      sx={{
        scrollMarginTop: SCROLL_MARGIN,
        mb: 3,
        px: 2.5,
        py: 1.5,
        borderRadius: 1.5,
        borderLeft: `6px solid ${group.color}`,
        bgcolor: alpha(group.color, 0.08),
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: group.color, fontWeight: 800, letterSpacing: 1 }}
      >
        {group.label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {group.description}
      </Typography>
    </Box>
  );
};

// ==============================
// Page
// ==============================
const UserGuide = () => {
  const { t } = useTranslation();
  const isDownLg = useIsDownLg();
  const isAdmin =
    JSON.parse(localStorage.getItem('aquatics_hub_2025_user') || 'null')
      ?.role === 'ADMIN';

  // States
  const [open, setOpen] = useState(true);
  const allowed = isAdmin ? ADMIN_AUDIENCES : COACH_AUDIENCES;
  const roleFilters = ROLE_FILTERS.filter(
    (f) => f.value === 'all' || allowed.includes(f.value),
  );
  const [role, setRole] = useState<RoleFilter>('all');
  const [activeId, setActiveId] = useState(
    () =>
      userGuideChapters.find((c) => allowed.includes(c.audience))?.id ??
      userGuideChapters[0].id,
  );
  const [query, setQuery] = useState('');
  const [tocOpen, setTocOpen] = useState(false);
  const [zoomed, setZoomed] = useState<UserGuideFigure | null>(null);
  const [showTop, setShowTop] = useState(false);
  const mainRef = useRef<HTMLElement | null>(null);
  // Set while a TOC click is smooth-scrolling, so the scroll-spy does not
  // overwrite the selected entry with the headings it passes on the way.
  const programmaticUntil = useRef(0);

  // TOC filtering: a chapter matches when its title or any of its content does;
  // inside it, only matching sections are listed.
  const visibleChapters = useMemo(
    () =>
      userGuideChapters.filter((chapter) =>
        isVisibleFor(chapter, role, allowed),
      ),
    [role, allowed],
  );

  const tocChapters = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visibleChapters;
    return visibleChapters
      .map((chapter) => {
        const chapterHit =
          plain(chapter.title).includes(q) ||
          chapter.blocks.some((b) => plain(blockText(b)).includes(q));
        const sections = chapter.sections.filter(
          (s) =>
            plain(s.title).includes(q) ||
            s.blocks.some((b) => plain(blockText(b)).includes(q)),
        );
        return chapterHit || sections.length ? { ...chapter, sections } : null;
      })
      .filter((c): c is UserGuideChapter => c !== null);
  }, [query, visibleChapters]);

  const scrollToId = useCallback((id: string, smooth = true) => {
    const element = document.getElementById(id);
    if (!element) return;
    programmaticUntil.current = Date.now() + 1000;
    element.scrollIntoView({
      behavior: smooth ? 'smooth' : 'auto',
      block: 'start',
    });
    setActiveId(id);
    window.history.replaceState(null, '', `#${id}`);
  }, []);

  const handleSelect = (id: string) => {
    scrollToId(id);
    // A group header highlights the group's first chapter.
    if (id.startsWith('grp-')) {
      const first = visibleChapters.find((c) => `grp-${c.audience}` === id);
      if (first) setActiveId(first.id);
    }
    if (isDownLg) setTocOpen(false);
  };

  const handleRoleChange = (value: RoleFilter) => {
    setRole(value);
    mainRef.current?.scrollTo({ top: 0 });
    window.history.replaceState(null, '', window.location.pathname);
  };

  // Deep link: /user-guide#c11-s5 opens at that section.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) setTimeout(() => scrollToId(id, false), 150);
  }, [scrollToId]);

  // Scroll-spy: highlight the heading nearest the top of the scroll container.
  useEffect(() => {
    const root = mainRef.current;
    if (!root) return undefined;
    const anchors = Array.from(
      root.querySelectorAll<HTMLElement>('[data-guide-anchor]'),
    );
    const onScroll = () => {
      setShowTop(root.scrollTop > 600);
      if (Date.now() < programmaticUntil.current) return;
      const limit = root.getBoundingClientRect().top + SCROLL_MARGIN + 24;
      let index = 0;
      anchors.some((anchor, i) => {
        if (anchor.getBoundingClientRect().top > limit) return true;
        index = i;
        return false;
      });
      // A group banner stands for the group's first chapter, right below it.
      const anchor = anchors[index]?.id.startsWith('grp-')
        ? anchors[index + 1]
        : anchors[index];
      const current = anchor?.id;
      if (current) setActiveId((prev) => (prev === current ? prev : current));
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => root.removeEventListener('scroll', onScroll);
  }, [visibleChapters]);

  const toc = (
    <GuideToc
      chapters={tocChapters}
      activeId={activeId}
      query={query}
      role={role}
      filters={roleFilters}
      onRoleChange={handleRoleChange}
      onQueryChange={setQuery}
      onSelect={handleSelect}
    />
  );

  // ==============================
  // Render
  // ==============================
  return (
    <div className="flex h-screen">
      {/* Sidebar */}
      {isAdmin ? (
        <MiniDrawer open={open} setOpen={setOpen} />
      ) : (
        <AssessmentMiniDrawer open={open} setOpen={setOpen} />
      )}

      {/* Main content */}
      <main ref={mainRef} className="flex-1 overflow-auto app-page-bg">
        <AppBarSpacer />

        <div className="px-6 py-4">
          <PageHeader
            title={t('userGuide.title')}
            subtitle={t('userGuide.subtitle', { version: USER_GUIDE_VERSION })}
          />

          {isDownLg && (
            <Accordion
              expanded={tocOpen}
              onChange={(_, value) => setTocOpen(value)}
              disableGutters
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <MenuBookIcon
                  fontSize="small"
                  sx={{ mr: 1 }}
                  color="secondary"
                />
                <Typography fontWeight={700}>Mục lục</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                {toc}
              </AccordionDetails>
            </Accordion>
          )}

          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            {!isDownLg && (
              <Paper
                variant="outlined"
                sx={{
                  width: 300,
                  flexShrink: 0,
                  position: 'sticky',
                  // The scroll container starts under the fixed 64px AppBar.
                  top: 72,
                  maxHeight: 'calc(100vh - 88px)',
                  overflow: 'auto',
                  p: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    pb: 1,
                  }}
                >
                  <MenuBookIcon fontSize="small" color="secondary" />
                  <Typography fontWeight={700}>Mục lục</Typography>
                </Box>
                {toc}
              </Paper>
            )}

            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                minWidth: 0,
                px: { xs: 2, md: 4 },
                py: 3,
                maxWidth: 1100,
              }}
            >
              {visibleChapters.map((chapter, chapterIndex) => (
                <Box component="section" key={chapter.id} sx={{ mb: 5 }}>
                  {visibleChapters[chapterIndex - 1]?.audience !==
                    chapter.audience && (
                    <AudienceBanner audience={chapter.audience} />
                  )}
                  <Typography
                    id={chapter.id}
                    data-guide-anchor
                    variant="h5"
                    color="secondary"
                    fontWeight={700}
                    sx={{
                      scrollMarginTop: SCROLL_MARGIN,
                      pt: 1,
                      pb: 1,
                      mb: 1.5,
                      borderBottom: 2,
                      borderColor: 'secondary.main',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>
                      {chapter.no}. {chapter.title}
                    </span>
                    <Chip
                      label={audienceOf(chapter.audience).label.split(' — ')[0]}
                      size="small"
                      sx={{
                        bgcolor: audienceOf(chapter.audience).color,
                        color: '#fff',
                        fontWeight: 700,
                      }}
                    />
                  </Typography>
                  {chapter.blocks.map((block, index) => (
                    <GuideBlockView
                      key={index}
                      block={block}
                      onZoom={setZoomed}
                    />
                  ))}
                  {chapter.sections.map((section) => (
                    <Box component="section" key={section.id} sx={{ mt: 3 }}>
                      <Typography
                        id={section.id}
                        data-guide-anchor
                        variant="h6"
                        fontWeight={700}
                        sx={{
                          scrollMarginTop: SCROLL_MARGIN,
                          mb: 1,
                          color: 'primary.dark',
                        }}
                      >
                        {section.title}
                      </Typography>
                      {section.blocks.map((block, index) => (
                        <GuideBlockView
                          key={index}
                          block={block}
                          onZoom={setZoomed}
                        />
                      ))}
                    </Box>
                  ))}
                </Box>
              ))}
            </Paper>
          </Box>
        </div>

        {showTop && (
          <Tooltip title="Về đầu trang">
            <Fab
              color="primary"
              size="medium"
              aria-label="Về đầu trang"
              onClick={() =>
                mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
              }
              sx={{ position: 'fixed', right: 24, bottom: 24 }}
            >
              <KeyboardArrowUpIcon />
            </Fab>
          </Tooltip>
        )}
      </main>

      {/* Screenshot zoom */}
      <Dialog
        open={!!zoomed}
        onClose={() => setZoomed(null)}
        maxWidth="xl"
        fullWidth
      >
        <IconButton
          aria-label="Đóng"
          onClick={() => setZoomed(null)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        {zoomed && (
          <DialogContent sx={{ p: 1 }}>
            <Box
              component="img"
              src={`${IMAGE_BASE}/${zoomed.img}.jpg`}
              alt={zoomed.caption}
              sx={{ display: 'block', width: '100%', height: 'auto' }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, textAlign: 'center' }}
            >
              <strong>{zoomed.label}.</strong> {zoomed.caption}
            </Typography>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

export default UserGuide;
